/**
 * Public careers page — ``/{org_slug}/careers`` and
 * ``/{org_slug}/careers/{job_id}``.
 *
 * Two read-only views:
 *
 *   * **Listing**  — branded with the org's name + logo, lists
 *     active public-facing jobs.
 *   * **Detail**   — single job with markdown description, apply
 *     form below.
 *
 * No auth, no sidebar — these pages render the bare branded
 * surface, indistinguishable from a hand-rolled careers site.
 * Public-facing means we have to be defensive on inputs (the
 * apply form). Backend rate-limits 5/IP/hr on the apply path.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, MapPin, Users } from "lucide-react";

import {
  applyToPublicJob,
  getPublicCareers,
  getPublicJob,
  type PublicCareersResponse,
  type PublicJobDetail,
  type PublicJobSummary,
} from "@/api/recruiting";


/**
 * Listing view: ``/:slug/careers``.
 *
 * Mounted under the slug parameter so the URL itself encodes the
 * tenant — no header / cookie required.
 */
export function PublicCareersListPage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<PublicCareersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getPublicCareers(slug)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError("This careers page isn't available.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return <PublicCareersError message={error} />;
  }
  if (!data) {
    return (
      <PublicShell>
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded bg-border/30 animate-pulse" />
          ))}
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell logo={data.org_logo_url} orgName={data.org_name}>
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold font-heading text-text-primary">
          Open roles at {data.org_name}
        </h1>
        <p className="text-text-muted mt-2">
          Help us build something great. We hire for ownership, curiosity, and
          high-quality work.
        </p>
      </div>

      {data.jobs.length === 0 ? (
        <div className="text-center text-text-muted py-12 rounded-lg border border-dashed border-border bg-background">
          <p className="text-sm">
            No openings right now. Check back soon!
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.jobs.map((j) => (
            <PublicJobRow key={j.id} job={j} slug={data.org_slug} />
          ))}
        </ul>
      )}
    </PublicShell>
  );
}


function PublicJobRow({ job, slug }: { job: PublicJobSummary; slug: string }) {
  return (
    <li>
      <Link
        to={`/${slug}/careers/${job.id}`}
        className="block rounded-xl border border-border bg-surface p-5 hover:border-accent/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold font-heading text-text-primary">
              {job.title}
            </h3>
            <div className="mt-1 flex items-center gap-3 text-xs text-text-muted">
              {job.department_name && (
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {job.department_name}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {job.is_remote
                  ? "Remote"
                  : job.location || "—"}
              </span>
              <span>{(job.commitment || "FULL_TIME").replace("_", " ")}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-accent font-medium">
            View role <ExternalLink className="h-3 w-3" />
          </div>
        </div>
      </Link>
    </li>
  );
}


// ════════════════════════════════════════════════════════════════════
// Detail view + apply form
// ════════════════════════════════════════════════════════════════════


export function PublicCareersDetailPage() {
  const { slug, jobId } = useParams<{ slug: string; jobId: string }>();
  const [job, setJob] = useState<PublicJobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !jobId) return;
    let cancelled = false;
    getPublicJob(slug, jobId)
      .then((j) => {
        if (!cancelled) setJob(j);
      })
      .catch(() => {
        if (!cancelled) setError("This role isn't available.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug, jobId]);

  if (error) return <PublicCareersError message={error} backTo={slug ? `/${slug}/careers` : "/"} />;
  if (!job) {
    return (
      <PublicShell>
        <div className="h-64 rounded bg-border/30 animate-pulse" />
      </PublicShell>
    );
  }

  return (
    <PublicShell logo={job.org_logo_url} orgName={job.org_name}>
      <Link
        to={`/${slug}/careers`}
        className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        All open roles
      </Link>

      <header className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-semibold font-heading text-text-primary">
          {job.title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-text-muted">
          {job.department_name && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-4 w-4" />
              {job.department_name}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {job.is_remote ? "Remote" : job.location || "—"}
          </span>
          <span>{(job.commitment || "FULL_TIME").replace("_", " ")}</span>
          {job.salary_min && job.salary_max && (
            <span>
              ${job.salary_min.toLocaleString()}–$
              {job.salary_max.toLocaleString()}{" "}
              {job.salary_currency || "USD"}
            </span>
          )}
        </div>
      </header>

      {/*
        Render the markdown description as preformatted text. We
        deliberately don't pull in a markdown renderer for the
        public surface — the JD generator outputs sane structure
        (## headings, lists) and the browser font renders this
        cleanly. Avoiding a markdown lib also keeps the public
        bundle tiny and reduces the XSS surface.
      */}
      {job.description && (
        <div className="rounded-xl border border-border bg-surface p-6 mb-8">
          <pre className="whitespace-pre-wrap text-sm text-text-primary font-sans">
            {job.description}
          </pre>
        </div>
      )}

      <ApplySection
        slug={slug || ""}
        jobId={job.id}
      />
    </PublicShell>
  );
}


function ApplySection({ slug, jobId }: { slug: string; jobId: string }) {
  const navigate = useNavigate();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!first.trim() || !last.trim() || !email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await applyToPublicJob(slug, jobId, {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        linkedin_url: linkedin.trim() || undefined,
        resume_url: resumeUrl.trim() || undefined,
        cover_letter: coverLetter.trim() || undefined,
      });
      setDone(true);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not submit your application — try again.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-success/40 bg-success/5 p-8 text-center">
        <h3 className="text-lg font-semibold text-text-primary">
          Application submitted
        </h3>
        <p className="text-sm text-text-muted mt-2">
          Thanks for applying — we'll be in touch.
        </p>
        <button
          onClick={() => navigate(`/${slug}/careers`)}
          className="mt-4 text-sm text-accent hover:underline"
        >
          See other open roles
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-border bg-surface p-6 space-y-4"
      noValidate
    >
      <h3 className="text-xl font-semibold font-heading text-text-primary">
        Apply for this role
      </h3>

      {error && (
        <div className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PublicField
          label="First name"
          value={first}
          onChange={setFirst}
          required
        />
        <PublicField
          label="Last name"
          value={last}
          onChange={setLast}
          required
        />
        <PublicField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          required
        />
        <PublicField
          label="Phone (optional)"
          value={phone}
          onChange={setPhone}
        />
        <PublicField
          label="LinkedIn URL (optional)"
          value={linkedin}
          onChange={setLinkedin}
          placeholder="https://linkedin.com/in/…"
        />
        <PublicField
          label="Resume URL (optional)"
          value={resumeUrl}
          onChange={setResumeUrl}
          placeholder="Link to your resume / portfolio"
        />
      </div>

      <div>
        <label
          htmlFor="cover-letter"
          className="block text-sm font-medium text-text-primary mb-1"
        >
          Cover letter (optional)
        </label>
        <textarea
          id="cover-letter"
          value={coverLetter}
          onChange={(e) => setCoverLetter(e.target.value)}
          rows={5}
          maxLength={10000}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          placeholder="Tell us why you're a fit."
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className={`inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white hover:bg-accent/90 ${
            submitting ? "opacity-60 cursor-wait" : ""
          }`}
        >
          {submitting ? "Submitting…" : "Submit application"}
        </button>
      </div>
    </form>
  );
}


function PublicField({
  label,
  value,
  onChange,
  required,
  type,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </label>
      <input
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
      />
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Layout
// ════════════════════════════════════════════════════════════════════


function PublicShell({
  children,
  logo,
  orgName,
}: {
  children: React.ReactNode;
  logo?: string | null;
  orgName?: string;
}) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt={orgName || ""} className="h-8 w-auto" />
            ) : null}
            {orgName && (
              <span className="text-base font-semibold font-heading text-text-primary">
                {orgName}
              </span>
            )}
          </div>
          <span className="text-xs text-text-muted">
            Powered by{" "}
            <a
              href="https://lenavio.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-text-primary underline"
            >
              Lenavio
            </a>
          </span>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-3xl mx-auto px-6 py-12"
      >
        {children}
      </motion.main>
    </div>
  );
}


function PublicCareersError({ message, backTo }: { message: string; backTo?: string }) {
  return (
    <PublicShell>
      <div className="text-center py-16">
        <h1 className="text-2xl font-semibold font-heading text-text-primary">
          Page unavailable
        </h1>
        <p className="text-sm text-text-muted mt-2">{message}</p>
        {backTo && (
          <Link to={backTo} className="text-accent hover:underline text-sm mt-4 inline-block">
            ← Back
          </Link>
        )}
      </div>
    </PublicShell>
  );
}
